import type { BalanceType, UserInfoType } from '../../../api/fetchers/userInfo/types';
import { useGameConfig } from '../../../config/GameConfigProvider';
import { type CoreRootState, useCoreSelector } from '../../CoreStore';

import type {
  CurrencyInfo,
  DealerType,
  TableCloseNotifyType,
  TableType,
  WinnersTotal,
  WinnerType,
  WinResultType,
} from './types';
import { GameStatus } from './types';

export const useWinners = (): WinnerType[] => {
  const winners = useCoreSelector(({ gameState }: CoreRootState) => gameState.winners);
  if (!Array.isArray(winners)) {
    // FIXME: if there is only one winner, the value received is an object, instead of an array. Adjust on server or on fetcher
    return winners ? [(winners as any).winner] : [];
  }
  return winners;
};

export const betsOpenStatuses: Array<GameStatus | string> = [GameStatus.PlaceYourBets, GameStatus.LastBets];

export const selectIsBetsOpen = ({ gameState }: CoreRootState): boolean =>
  betsOpenStatuses.includes(gameState.currentStatus);

export const useIsBettingAllowedConnected = (): boolean => {
  const customSelector = useGameConfig().config.bettingAllowedSelector;
  return useCoreSelector(customSelector ?? selectIsBetsOpen);
};

export const deductibleBalanceSelector = (state: CoreRootState): number => {
  return (state.gameState.userInfo?.balance.amount || 0) + (state.gameState.userInfo?.balance.bonus || 0);
};

export const selectHideUI = ({ gameState }: CoreRootState): boolean => gameState.hideUI;
export const selectWinners = ({ gameState }: CoreRootState): WinnerType[] => gameState.winners;
export const selectWinnersTotal = ({ gameState }: CoreRootState): WinnersTotal | null => gameState.winnersTotal;
export const selectWinResult = ({ gameState }: CoreRootState): WinResultType | null => gameState.winResult;
export const selectCurrentStatus = ({ gameState }: CoreRootState): GameStatus | string => gameState.currentStatus;
export const selectTimer = ({ gameState }: CoreRootState): number => gameState.timer;
export const selectTimerStartTime = ({ gameState }: CoreRootState): number => gameState.timerStartTime;
export const selectGameId = ({ gameState }: CoreRootState): string | null => gameState.gameId;
export const selectGameChannel = ({ gameState }: CoreRootState): string | null => gameState.gameChannel;
export const selectGameName = ({ gameState }: CoreRootState): string | null => gameState.gameName;
export const selectGameSubscribed = ({ gameState }: CoreRootState): boolean => !!gameState.gameChannel;
export const selectUserInfoReady = ({ gameState }: CoreRootState): boolean => !!gameState.userInfo;
export const selectUserInfo = ({ gameState }: CoreRootState): UserInfoType | null => gameState.userInfo;
export const selectUserScreenName = ({ gameState }: CoreRootState): string | undefined =>
  gameState.userInfo?.screenName;
export const selectUserBalance = ({ gameState }: CoreRootState): BalanceType | undefined => gameState.userInfo?.balance;
export const selectDeductibleBalance = ({ gameState }: CoreRootState): number =>
  (gameState.userInfo?.balance.amount ?? 0) + (gameState.userInfo?.balance.bonus ?? 0);
export const selectCurrency = ({ gameState }: CoreRootState): string | null => gameState.currency;
export const selectNetPosition = ({ gameState }: CoreRootState): number => gameState.netPosition;
export const selectCurrencyInfo = ({ gameState }: CoreRootState): CurrencyInfo | null => gameState.currencyInfo;
export const selectLeftDrawerOpened = ({ gameState }: CoreRootState): string | null => gameState.leftDrawerOpened;
export const selectDealer = ({ gameState }: CoreRootState): DealerType | null => gameState.dealer;
export const selectGameTable = ({ gameState }: CoreRootState): TableType | null => gameState.table;
export const selectIsGamePaused = ({ gameState }: CoreRootState): boolean => gameState.gamePaused;
export const selectIsGameAbandoned = ({ gameState }: CoreRootState): boolean => gameState.gameAbandoned;
export const selectIsGameSuspended = ({ gameState }: CoreRootState): boolean => gameState.gameSuspended;
export const selectIsLoadedInActiveRound = ({ gameState }: CoreRootState): boolean => gameState.isLoadedInActiveRound;
export const selectVideoPoster = ({ gameState }: CoreRootState): string => gameState.videoPoster;
export const selectTableCloseNotify = ({ gameState }: CoreRootState): TableCloseNotifyType | null =>
  gameState.tableCloseNotify;
export const selectCurrencyDecimals = ({ gameState }: CoreRootState): number =>
  gameState.currencyInfo?.digitsNumber ?? 2;

export const useGameStateSlice = {
  hideUI: (): boolean => {
    return useCoreSelector(selectHideUI);
  },
  winners: (): WinnerType[] => {
    return useCoreSelector(selectWinners);
  },
  getWinnersTotal: (): WinnersTotal | null => {
    return useCoreSelector(selectWinnersTotal);
  },
  winResult: (): WinResultType | null => {
    return useCoreSelector(selectWinResult);
  },
  getCurrentStatus: (): GameStatus | string => {
    return useCoreSelector(selectCurrentStatus);
  },
  getTimer: (): number => {
    return useCoreSelector(selectTimer);
  },
  getTimerStartTime: (): number => {
    return useCoreSelector(selectTimerStartTime);
  },
  isBetsOpenStatus: (): boolean => {
    return useCoreSelector(selectIsBetsOpen);
  },
  gameId: (): string | null => {
    return useCoreSelector(selectGameId);
  },
  gameChannel: (): string | null => {
    return useCoreSelector(selectGameChannel);
  },
  gameName: (): string | null => {
    return useCoreSelector(selectGameName);
  },
  getGameSubscribed: (): boolean => {
    return useCoreSelector(selectGameSubscribed);
  },
  userInfoReady: (): boolean => {
    return useCoreSelector(selectUserInfoReady);
  },
  userInfo: (): UserInfoType | null => {
    return useCoreSelector(selectUserInfo);
  },
  userName: (): string | undefined => {
    return useCoreSelector(selectUserScreenName);
  },
  balance: (): BalanceType | undefined => {
    return useCoreSelector(selectUserBalance);
  },
  getDeductibleBalance: (): number => {
    return useCoreSelector(selectDeductibleBalance);
  },
  getCurrency: (): string | null => {
    return useCoreSelector(selectCurrency);
  },
  getNetPosition: (): number => {
    return useCoreSelector(selectNetPosition);
  },
  getCurrencyInfo: (): CurrencyInfo | null => {
    return useCoreSelector(selectCurrencyInfo);
  },
  getCurrencyDecimals: (): number => {
    return useCoreSelector(selectCurrencyDecimals);
  },
  getOpenedLeftDrawer: (): string | null => {
    return useCoreSelector(selectLeftDrawerOpened);
  },
  dealer: (): DealerType | null => {
    return useCoreSelector(selectDealer);
  },
  table: (): TableType | null => {
    return useCoreSelector(selectGameTable);
  },
  getIsGamePaused: (): boolean => {
    return useCoreSelector(selectIsGamePaused);
  },
  getIsGameAbandoned: (): boolean => {
    return useCoreSelector(selectIsGameAbandoned);
  },
  getIsGameSuspended: (): boolean => {
    return useCoreSelector(selectIsGameSuspended);
  },
  isLoadedInActiveRound: (): boolean => {
    return useCoreSelector(selectIsLoadedInActiveRound);
  },
  getVideoPoster: (): string => {
    return useCoreSelector(selectVideoPoster);
  },
  getTableCloseNotification: (): TableCloseNotifyType | null => {
    return useCoreSelector(selectTableCloseNotify);
  },
};
